import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { billingAuthHeaders } from '../utils/billingApi';
import { CARD_ONLY_PAYMENT_ELEMENT_OPTIONS } from '../utils/stripePaymentElementOptions';
import {
    confirmPaymentElementSetup,
    consumeOwnedStripeSetupRedirect,
    rememberExpectedSetupIntent,
    withoutStripeSetupRedirectParams,
} from '../utils/stripeSetupConfirm';

const billingApiRoot = (backendUrl) => backendUrl.replace(/\/contract\/?$/, '');
const DEFAULT_PLAN = 'keyflo_tc_low_monthly';

function formatBrand(brand) {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function PaymentForm({ clientSecret, submitLabel, onSuccess, onError, onCancel }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        try {
            const { error, setupIntent, intentId, alreadySucceeded } = await confirmPaymentElementSetup(
                stripe,
                elements,
                { clientSecret },
            );

            if (error && !alreadySucceeded) {
                onError(error.message);
                return;
            }

            if (intentId && (setupIntent?.status === 'succeeded' || alreadySucceeded)) {
                await onSuccess(intentId);
            } else {
                onError('Could not verify card setup. Please refresh and try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={CARD_ONLY_PAYMENT_ELEMENT_OPTIONS} />
            <div className="payment-method-form-actions">
                {onCancel && (
                    <button type="button" className="payment-method-cancel-btn" onClick={onCancel} disabled={submitting}>
                        Cancel
                    </button>
                )}
                <button
                    type="submit"
                    disabled={!stripe || submitting}
                    style={{
                        padding: '10px 20px',
                        background: '#7fb069',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        cursor: submitting ? 'wait' : 'pointer',
                    }}
                >
                    {submitting ? 'Saving…' : submitLabel}
                </button>
            </div>
        </form>
    );
}

export default function ProfilePaymentMethods({
    user,
    backendUrl,
    sessionToken = null,
    onUpdated = null,
}) {
    const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const [clientSecret, setClientSecret] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);
    const [cardOnFile, setCardOnFile] = useState(null);
    const [paymentSetupAllowed, setPaymentSetupAllowed] = useState(true);
    const [isEditingCard, setIsEditingCard] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [syncRetrying, setSyncRetrying] = useState(false);
    const [productType, setProductType] = useState(null);
    const [paymentMethodStatus, setPaymentMethodStatus] = useState(null);
    const productTypeRef = useRef(productType);
    const magicSetupStartedRef = useRef(false);
    const redirectHandledRef = useRef(false);
    productTypeRef.current = productType;
    const billingBase = `${billingApiRoot(backendUrl)}/billing`;
    const isMagicLink = Boolean(sessionToken);
    const goToAccountTab = () => {
        const next = { tab: 'account' };
        ['referral_token', 'referral_send_id'].forEach((key) => {
            const value = searchParams.get(key);
            if (value) {
                next[key] = value;
            }
        });
        setSearchParams(next);
    };

    const authHeaders = useCallback(async () => {
        if (sessionToken) return {};
        return billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
    }, [sessionToken, getAccessTokenSilently, getIdTokenClaims]);

    const sessionRoot = sessionToken
        ? `${billingBase}/payment-method-sessions/${sessionToken}`
        : null;

    const loadBillingContext = useCallback(async () => {
        if (sessionToken) {
            const res = await axios.get(sessionRoot);
            const plan = res.data.product_type || DEFAULT_PLAN;
            productTypeRef.current = plan;
            setProductType(plan);
            return plan;
        }
        if (!user?.email) return DEFAULT_PLAN;
        try {
            const headers = await authHeaders();
            const res = await axios.get(
                `${billingBase}/subscription-summary?email=${encodeURIComponent(user.email)}`,
                { headers },
            );
            const plan = res.data.product_type || DEFAULT_PLAN;
            productTypeRef.current = plan;
            setProductType(plan);
            return plan;
        } catch {
            setProductType(DEFAULT_PLAN);
            return DEFAULT_PLAN;
        }
    }, [billingBase, user?.email, authHeaders, sessionToken, sessionRoot]);

    const loadMethods = useCallback(async () => {
        if (!sessionToken && !user?.email) return { card: null, status: null, syncError: null };
        const headers = await authHeaders();
        const url = sessionToken
            ? `${sessionRoot}/payment-methods`
            : `${billingBase}/payment-methods?email=${encodeURIComponent(user.email)}`;
        const res = await axios.get(url, { headers });
        const methods = res.data.payment_methods || [];
        const status = res.data.payment_method_status || null;
        const syncError = res.data.sync_error || null;
        const setupAllowed = res.data.payment_setup_allowed !== false;
        const card = methods[0] || null;
        setCardOnFile(card);
        setPaymentSetupAllowed(setupAllowed);
        setPaymentMethodStatus(status);
        return { card, status, syncError, setupAllowed };
    }, [billingBase, user?.email, authHeaders, sessionToken, sessionRoot]);

    const retryBillingSync = useCallback(async () => {
        if (!sessionToken && !user?.email) return;
        setSyncRetrying(true);
        setError(null);
        setMessage(null);
        try {
            const headers = await authHeaders();
            const url = sessionToken
                ? `${sessionRoot}/confirm`
                : `${billingBase}/payment-method/confirm`;
            await axios.post(
                url,
                sessionToken
                    ? {}
                    : { email: user.email, product_type: productType || DEFAULT_PLAN },
                { headers },
            );
            if (user?.email) {
                sessionStorage.removeItem(`keyflo_access_status_${user.email}`);
                sessionStorage.removeItem(`keyflo_access_data_${user.email}`);
            }
            if (sessionToken && typeof onUpdated === 'function') {
                setMessage('Payment method synced.');
                onUpdated();
                return;
            }
            const { card, status, syncError } = await loadMethods();
            if (syncError) {
                setError(`Your card is saved in Stripe, but KeyFlo could not finish syncing: ${syncError}`);
                return;
            }
            if (card && status !== 'verified') {
                setError('Your card is saved in Stripe, but KeyFlo has not marked it verified yet. Please try again.');
                return;
            }
            setMessage('Payment method synced.');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Could not sync payment method.');
        } finally {
            setSyncRetrying(false);
        }
    }, [billingBase, user?.email, loadMethods, authHeaders, productType, sessionToken, sessionRoot, onUpdated]);

    const completeBillingSetup = useCallback(async (
        setupIntentId = null,
        hadCard = false,
        { retainSetupIntentPlan = false } = {},
    ) => {
        setError(null);
        const headers = await authHeaders();
        const url = sessionToken
            ? `${sessionRoot}/confirm`
            : `${billingBase}/payment-method/confirm`;
        const body = {};
        if (!sessionToken) {
            body.email = user.email;
            if (!retainSetupIntentPlan) {
                body.product_type = productTypeRef.current || productType || DEFAULT_PLAN;
            }
        }
        if (setupIntentId) {
            body.setup_intent_id = setupIntentId;
        }
        await axios.post(url, body, { headers });
        setMessage(
            hadCard
                ? 'Payment method updated.'
                : 'Payment method saved. Your free trial has started.',
        );
        if (user?.email) {
            sessionStorage.removeItem(`keyflo_access_status_${user.email}`);
            sessionStorage.removeItem(`keyflo_access_data_${user.email}`);
        }
        setIsEditingCard(false);
        setClientSecret(null);
        if (sessionToken && typeof onUpdated === 'function') {
            onUpdated();
            return;
        }
        await loadMethods();
    }, [billingBase, user?.email, loadMethods, authHeaders, productType, sessionToken, sessionRoot, onUpdated]);

    const beginSetupIntent = useCallback(async (planType) => {
        if (!paymentSetupAllowed) return;
        const resolvedPlan = typeof planType === 'string' && planType
            ? planType
            : (productTypeRef.current || DEFAULT_PLAN);
        const headers = await authHeaders();
        const url = sessionToken
            ? `${sessionRoot}/setup-intent`
            : `${billingBase}/setup-intent`;
        const si = await axios.post(
            url,
            sessionToken
                ? {}
                : { email: user.email, product_type: resolvedPlan },
            { headers },
        );
        rememberExpectedSetupIntent(si.data.client_secret || si.data.setup_intent_id);
        setClientSecret(si.data.client_secret);
        setIsEditingCard(true);
        setError(null);
        setMessage(null);
    }, [billingBase, user?.email, authHeaders, paymentSetupAllowed, sessionToken, sessionRoot]);

    const retrySetupIntent = useCallback(async () => {
        magicSetupStartedRef.current = false;
        redirectHandledRef.current = false;
        setError(null);
        setSyncRetrying(true);
        try {
            await beginSetupIntent();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Could not start payment setup.');
        } finally {
            setSyncRetrying(false);
        }
    }, [beginSetupIntent]);

    const retryPaymentAction = useCallback(async () => {
        if (isMagicLink && !cardOnFile && !clientSecret) {
            await retrySetupIntent();
            return;
        }
        await retryBillingSync();
    }, [isMagicLink, cardOnFile, clientSecret, retrySetupIntent, retryBillingSync]);

    useEffect(() => {
        const init = async () => {
            try {
                const cfg = await axios.get(`${billingBase}/config`);
                setPublishableKey(cfg.data.publishable_key);
                if (!cfg.data.stripe_configured) {
                    setError('Billing is not configured in this environment.');
                    return;
                }
                const stripeRedirect = consumeOwnedStripeSetupRedirect(searchParams);
                if (searchParams.get('setup_intent') || searchParams.get('redirect_status')) {
                    setSearchParams(withoutStripeSetupRedirectParams(searchParams), { replace: true });
                }
                if (stripeRedirect && !redirectHandledRef.current) {
                    redirectHandledRef.current = true;
                    if (stripeRedirect.succeeded && stripeRedirect.setupIntentId) {
                        try {
                            await loadBillingContext();
                            const { card } = await loadMethods();
                            await completeBillingSetup(
                                stripeRedirect.setupIntentId,
                                Boolean(card),
                                { retainSetupIntentPlan: true },
                            );
                        } catch (err) {
                            redirectHandledRef.current = false;
                            setError(err.response?.data?.error || err.message || 'Could not finish card setup.');
                        }
                        return;
                    }
                    redirectHandledRef.current = false;
                    setError('Card authentication did not complete. Please try again.');
                } else if (redirectHandledRef.current) {
                    await loadBillingContext();
                    await loadMethods();
                    return;
                }
                const plan = await loadBillingContext();
                const { card, status, syncError, setupAllowed } = await loadMethods();
                if (!setupAllowed) {
                    setIsEditingCard(false);
                    setClientSecret(null);
                    return;
                }
                if (syncError) {
                    setError(`Your card is saved in Stripe, but KeyFlo could not finish syncing: ${syncError}`);
                } else if (card && status !== 'verified') {
                    setError('Your card is saved in Stripe, but KeyFlo has not marked it verified yet. Try syncing again.');
                }
                if (!card && status !== 'verified') {
                    if (isMagicLink) {
                        if (!magicSetupStartedRef.current) {
                            magicSetupStartedRef.current = true;
                            try {
                                await beginSetupIntent(plan);
                            } catch (err) {
                                magicSetupStartedRef.current = false;
                                throw err;
                            }
                        }
                    } else {
                        setIsEditingCard(false);
                        setClientSecret(null);
                    }
                } else if (!card) {
                    await beginSetupIntent(plan);
                } else {
                    setIsEditingCard(false);
                    setClientSecret(null);
                }
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            }
        };
        if (sessionToken || user?.email) init();
    }, [
        user?.email,
        billingBase,
        loadMethods,
        loadBillingContext,
        beginSetupIntent,
        sessionToken,
        isMagicLink,
        searchParams,
        setSearchParams,
        completeBillingSetup,
    ]);

    const onSuccess = async (setupIntentId) => {
        try {
            await completeBillingSetup(setupIntentId, Boolean(cardOnFile));
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingCard(false);
        setClientSecret(null);
        setError(null);
    };

    const stripePromise = publishableKey ? loadStripe(publishableKey) : null;
    const paymentVerified = paymentMethodStatus === 'verified';
    const allowCardUpdate = isMagicLink || paymentVerified;
    const needsSignupWizard = !isMagicLink && paymentSetupAllowed && !paymentVerified && !cardOnFile;
    const showPaymentForm = paymentSetupAllowed && isEditingCard && clientSecret && stripePromise && allowCardUpdate;

    return (
        <div className="profile-tab-panel">
            {!isMagicLink && <h2>Payment Method</h2>}
            {!paymentSetupAllowed && (
                <p style={{ color: '#374151', lineHeight: 1.6 }}>
                    You&apos;re on a complimentary beta account. Payment method setup is not required.
                </p>
            )}
            {paymentSetupAllowed && error && (
                <div className="payment-method-sync-error">
                    <p>{error}</p>
                    <button
                        type="button"
                        className="payment-method-change-btn"
                        onClick={retryPaymentAction}
                        disabled={syncRetrying}
                    >
                        {syncRetrying
                            ? 'Retrying…'
                            : (isMagicLink && !cardOnFile && !clientSecret ? 'Retry setup' : 'Retry sync')}
                    </button>
                </div>
            )}
            {paymentSetupAllowed && message && <p style={{ color: '#166534' }}>{message}</p>}

            {needsSignupWizard && (
                <p style={{ color: '#64748b', maxWidth: '520px', lineHeight: 1.6 }}>
                    Complete your profile and add a payment method using the signup wizard on the{' '}
                    <button
                        type="button"
                        onClick={goToAccountTab}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#7fb069',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                    >
                        Account
                    </button>{' '}
                    tab. You can choose your subscription plan after your payment method is verified.
                </p>
            )}

            {paymentSetupAllowed && cardOnFile && !isEditingCard && (
                <div style={{ marginBottom: '24px' }}>
                    <p className="payment-method-section-label">Card on File</p>
                    <div className="payment-method-card">
                        <div className="payment-method-card-details">
                            <span className="payment-method-card-brand">{formatBrand(cardOnFile.brand)}</span>
                            <span className="payment-method-card-number">•••• •••• •••• {cardOnFile.last4}</span>
                            <span className="payment-method-card-expiry">
                                Expires {String(cardOnFile.exp_month).padStart(2, '0')}/{String(cardOnFile.exp_year).slice(-2)}
                            </span>
                        </div>
                        {allowCardUpdate && (
                            <button
                                type="button"
                                className="payment-method-change-btn"
                                onClick={() => beginSetupIntent()}
                            >
                                Change card
                            </button>
                        )}
                    </div>
                </div>
            )}

            {showPaymentForm && (
                <div className="payment-method-form-panel">
                    <p className="payment-method-section-label">
                        {cardOnFile ? 'Update card on file' : 'Add card on file'}
                    </p>
                    <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
                        <PaymentForm
                            clientSecret={clientSecret}
                            submitLabel={cardOnFile ? 'Update card' : 'Save card'}
                            onSuccess={onSuccess}
                            onError={setError}
                            onCancel={cardOnFile ? handleCancelEdit : null}
                        />
                    </Elements>
                </div>
            )}
        </div>
    );
}
